import type { ApiTypes, Schemas } from "@fluid-app/api-client";
import { EventTypes, startedCheckout, visitPage } from "@fluid-app/api-client";
import { STORAGE_KEYS } from "@fluid-app/storage-manager";

import type { AttributionManager } from "./AttributionManager";
import type {
  CheckoutStartedEventWithData,
  FairshareConfig,
  PageVisitEventWithData,
  TrackableEventWithData,
} from "./types";
import { debug } from "../utils/debug";
import { getBrowserInfo } from "../utils/environment";

// Default values for performance optimization
const DEFAULT_BATCH_SIZE = 10; // Maximum number of events to send in a single batch
const DEFAULT_BATCH_INTERVAL = 2000; // Time in ms to wait before sending a batch
const DEFAULT_MAX_QUEUE_SIZE = 100; // Maximum number of events to store in the queue

/**
 * Manages event tracking and batching for the FairShare SDK
 */
export class EventManager {
  private eventQueue: TrackableEventWithData[] = [];
  private batchTimeout: ReturnType<typeof setTimeout> | null = null;
  private processingBatch = false;
  private retryCount: number;
  private fluidShop: string;
  private storageManager: {
    getItem: (key: string) => string | null;
    setItem: (key: string, value: string) => void;
  };
  private sessionManager: { getSessionToken: () => string | null };
  private attributionManager: AttributionManager;
  private userId: string | null = null;
  private config: FairshareConfig;
  private flushInterval?: ReturnType<typeof setInterval>;
  private batchSize: number;
  private batchInterval: number;
  private maxQueueSize: number;

  /**
   * Create a new EventManager instance
   * @param storageManager Storage manager instance
   * @param sessionManager Session manager instance
   * @param attributionManager Attribution manager instance
   * @param fingerPrintManager FingerPrintManager instance
   * @param fluidShop The fluid shop identifier
   * @param retryCount Number of retry attempts
   * @param config Configuration for Fairshare
   */
  constructor(
    storageManager: {
      getItem: (key: string) => string | null;
      setItem: (key: string, value: string) => void;
    },
    sessionManager: { getSessionToken: () => string | null },
    attributionManager: AttributionManager,
    fluidShop = "",
    retryCount = 3,
    config: FairshareConfig,
  ) {
    this.storageManager = storageManager;
    this.sessionManager = sessionManager;
    this.attributionManager = attributionManager;
    this.fluidShop = fluidShop;
    this.retryCount = retryCount;
    this.config = config;

    // Set configurable batch settings or use defaults
    this.batchSize = config.batchSize ?? DEFAULT_BATCH_SIZE;
    this.batchInterval = config.batchInterval ?? DEFAULT_BATCH_INTERVAL;
    this.maxQueueSize = config.maxQueueSize ?? DEFAULT_MAX_QUEUE_SIZE;

    // Load any queued events from storage
    this.loadQueuedEvents();
    this.setupFlushInterval();
  }

  /**
   * Clean up resources when the manager is no longer needed
   */
  public cleanup(): void {
    // Clear any pending timeouts
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
      this.batchTimeout = null;
    }

    // Clear flush interval if set
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = undefined;
    }

    // Process any remaining events before cleanup
    void this.flushQueue(false);
  }

  /**
   * Track a page visit event
   */
  public trackPageVisit(): void {
    try {
      // Check if session token exists
      const sessionToken = this.sessionManager.getSessionToken();
      if (!sessionToken) {
        debug("Cannot track page visit: No session token available");
        return;
      }

      // Force a fresh attribution capture before tracking the page visit
      this.attributionManager.getAttributionData();

      const { url, title } = getBrowserInfo();

      const data = {
        url,
        timestamp: Date.now(),
      };

      const metadata = this.buildMetadata();

      // Double-check metadata has a session token
      if (!metadata.fluid_session) {
        debug("Cannot track page visit: Missing session token in metadata");
        return;
      }

      debug("Page visit metadata with attribution:", metadata);
      this.queueEvent({
        data: {
          page: {
            url,
            title,
            // description,
            // image,
          },
          metadata,
        },
        eventName: EventTypes.PAGE_VISIT,
      });
      debug("Page visit tracked", data);
    } catch (error) {
      debug(
        "Error tracking page visit",
        error instanceof Error ? error.message : String(error),
      );
    }
  }

  /**
   * Track a checkout started event
   */
  public trackCheckoutStarted(
    data: ApiTypes["checkout-started"]["request"],
  ): void {
    try {
      // Ensure we have fresh attribution data before tracking any event
      this.attributionManager.getAttributionData();
      debug("Refreshed attribution data before tracking checkout started");

      const metadata = this.buildMetadata();

      this.queueEvent({
        data: {
          cart_token: data.cart_token,
          metadata,
        },
        eventName: EventTypes.CHECKOUT_STARTED,
      });
      debug("Checkout started event tracked", data);
    } catch (error) {
      debug(
        "Error tracking checkout started event",
        error instanceof Error ? error.message : String(error),
      );
    }
  }

  /**
   * Track a custom event
   * @param eventName Event name
   * @param properties Event properties
   */
  public trackEvent(event: TrackableEventWithData): void {
    try {
      // Ensure we have fresh attribution data before tracking any event
      this.attributionManager.getAttributionData();
      debug(
        "Refreshed attribution data before tracking event:",
        event.eventName,
      );

      this.queueEvent(event);
      debug("Event tracked", event);
    } catch (error) {
      debug(
        "Error tracking custom event",
        error instanceof Error ? error.message : String(error),
      );
    }
  }

  /**
   * Set the user ID
   * @param userId User ID
   */
  public setUserId(userId: string): void {
    this.userId = userId;
  }

  /**
   * Get the user ID
   * @returns The user ID or an empty string if not set
   */
  public getUserId(): string {
    return this.userId ?? "";
  }

  /**
   * Build metadata for API requests
   * @returns Metadata object with session, attribution, and other data
   */
  private buildMetadata(): Schemas["RequestMetadata"] {
    const sessionToken = this.sessionManager.getSessionToken();

    if (!sessionToken) {
      debug("Warning: No session token available when building metadata");
    } else {
      debug("Using session token for metadata:", sessionToken);
    }

    const metadata: Schemas["RequestMetadata"] = {
      fluid_session: sessionToken ?? "",
      fluid_shop: this.fluidShop,
    };

    // Capture fresh attribution directly from URL at event time
    // and only include it if a share GUID is present
    const attributionLookup =
      this.attributionManager.convertToAttributionLookup();
    if (attributionLookup?.shareGuid) {
      debug(
        `Adding attribution data with share_guid: ${attributionLookup.shareGuid}`,
      );
      metadata.attribution = {
        share_guid: attributionLookup.shareGuid,
      };
    } else {
      debug(
        "No attribution data found in URL, skipping attribution in request",
      );
    }

    return metadata;
  }

  /**
   * Queue an event for batched sending
   * @param type Event type
   * @param data Event data
   * @param metadata Event metadata
   */
  private queueEvent(event: TrackableEventWithData): void {
    // Add to the queue
    this.eventQueue.push(event);

    // Enforce maximum queue size by removing oldest events if needed
    if (this.eventQueue.length > this.maxQueueSize) {
      this.eventQueue = this.eventQueue.slice(-this.maxQueueSize);
    }

    // Save queue to storage
    this.saveQueuedEvents();

    // Schedule batch processing if not already scheduled
    this.scheduleBatch();
  }

  /**
   * Schedule batch processing
   */
  private scheduleBatch(): void {
    if (this.batchTimeout === null && this.eventQueue.length > 0) {
      this.batchTimeout = setTimeout(() => {
        this.processBatch();
        // Clear the timeout reference after it's executed
        this.batchTimeout = null;
      }, this.batchInterval);

      debug(
        `Batch scheduled to process in ${this.batchInterval}ms, queue size: ${this.eventQueue.length}`,
      );
    }
  }

  /**
   * Process the event queue
   */
  private processBatch(): void {
    // If already processing a batch, skip
    if (this.processingBatch) {
      return;
    }

    // Set processing flag
    this.processingBatch = true;

    try {
      // Get events to process (up to batchSize)
      const eventsToProcess = this.eventQueue.slice(0, this.batchSize);

      // If no events to process, return
      if (eventsToProcess.length === 0) {
        this.processingBatch = false;
        return;
      }

      // Send events to server
      const success = this.sendBatch(eventsToProcess);

      if (success) {
        // Remove processed events from queue
        this.eventQueue = this.eventQueue.slice(eventsToProcess.length);

        // Save updated queue
        this.saveQueuedEvents();

        // If more events in queue, schedule another batch
        if (this.eventQueue.length > 0) {
          this.scheduleBatch();
        }
      } else {
        // Retry processing the batch
        if (this.retryCount > 0) {
          this.retryCount--;
          this.scheduleBatch();
        }
      }
    } catch (error) {
      debug(
        "Error processing batch",
        error instanceof Error ? error.message : String(error),
      );
    } finally {
      // Reset processing flag
      this.processingBatch = false;
    }
  }

  /**
   * Send a batch of events to the server
   * @param events Events to send
   * @returns Whether the send was successful
   */
  private sendBatch(events: TrackableEventWithData[]): boolean {
    try {
      // Log batch events for debugging
      debug("Sending batch events", events);

      // Force a fresh attribution capture before processing events
      this.attributionManager.getAttributionData();
      debug("Refreshed attribution data before sending batch");

      // For each event, ensure it has the latest metadata
      const updatedEvents = events.map((event) => {
        // Get fresh metadata with the most current attribution data
        const freshMetadata = this.buildMetadata();

        // Merge the fresh metadata with the event's existing metadata
        // This ensures we always send the most up-to-date attribution data
        return {
          ...event,
          metadata: {
            ...event.data.metadata,
            ...freshMetadata,
          },
        };
      });

      // Process each event based on its type
      for (const event of updatedEvents) {
        void this.processEvent(event);
      }

      // Log each event for debugging purposes
      updatedEvents.forEach((event) => {
        debug(`Processed ${event.eventName} event`, {
          eventName: event.eventName,
          data: event.data,
        });
      });

      return true;
    } catch (error) {
      debug(
        "Error sending batch events",
        error instanceof Error ? error.message : String(error),
      );
      return false;
    }
  }

  /**
   * Process an individual event based on its type
   * @param event The event to process
   * @returns Promise that resolves to true if successful, false otherwise
   */
  private async processEvent(event: TrackableEventWithData): Promise<boolean> {
    try {
      debug(`Processing ${event.eventName} event...`);

      let result = false;

      switch (event.eventName) {
        case EventTypes.PAGE_VISIT:
          result = await this.processPageVisit(event);
          break;
        case EventTypes.CHECKOUT_STARTED:
          result = await this.processCheckoutStarted(event);
          break;
        default:
          debug(`Unsupported event type: ${event.eventName}`);
          return false;
      }

      debug(
        `Completed processing ${event.eventName} event with result: ${result}`,
      );
      return result;
    } catch (error) {
      // Avoid accessing event.eventName directly to prevent TypeScript errors
      debug(
        `Error processing event`,
        error instanceof Error ? error.message : String(error),
      );
      return false;
    }
  }

  /**
   * Process a page visit event
   * @param event Page visit event
   * @returns Promise that resolves to true if successful, false otherwise
   */
  private async processPageVisit(
    event: PageVisitEventWithData,
  ): Promise<boolean> {
    try {
      const { page, metadata } = event.data;

      const requestBody = {
        page,
        metadata,
      };

      debug("Sending page visit request:", requestBody);

      // Ensure we wait for the complete response
      const response = await visitPage({
        request: requestBody,
      });

      debug("Page visit tracked successfully", response);
      return true;
    } catch (error) {
      debug(
        "Error tracking page visit",
        error instanceof Error ? error.message : String(error),
      );
      return false;
    }
  }

  /**
   * Process a checkout started event
   * @param event Checkout started event
   * @returns Promise that resolves to true if successful, false otherwise
   */
  private async processCheckoutStarted(
    event: CheckoutStartedEventWithData,
  ): Promise<boolean> {
    try {
      const requestBody: ApiTypes["checkout-started"]["request"] = {
        cart_token: event.data.cart_token,
        metadata: event.data.metadata,
      };

      debug("Sending checkout started request:", requestBody);

      // Make sure we explicitly wait for the response before proceeding
      const response = await startedCheckout({
        request: requestBody,
      });

      debug("Checkout started tracked successfully", response);
      return true;
    } catch (error) {
      debug(
        "Error tracking checkout started",
        error instanceof Error ? error.message : String(error),
      );
      return false;
    }
  }

  /**
   * Save queued events to storage
   */
  private saveQueuedEvents(): void {
    try {
      this.storageManager.setItem(
        STORAGE_KEYS.EVENT_QUEUE,
        JSON.stringify(this.eventQueue),
      );
    } catch (error) {
      debug(
        "Error saving queued events",
        error instanceof Error ? error.message : String(error),
      );
    }
  }

  /**
   * Load queued events from storage
   */
  private loadQueuedEvents(): void {
    try {
      const queuedEvents = this.storageManager.getItem(
        STORAGE_KEYS.EVENT_QUEUE,
      );
      if (queuedEvents) {
        this.eventQueue = JSON.parse(queuedEvents) as TrackableEventWithData[];
      }
    } catch (error) {
      debug(
        "Error loading queued events",
        error instanceof Error ? error.message : String(error),
      );
    }
  }

  /**
   * Flush the event queue
   * @param sync Whether to flush synchronously (wait for all events to complete)
   * @param timeoutMs Maximum time to wait for events to complete (only applies when sync=true)
   * @returns Promise that resolves when all events are processed
   */
  public async flushQueue(sync = false, timeoutMs = 3000): Promise<void> {
    // If no events in queue, return resolved promise immediately
    if (this.eventQueue.length === 0) {
      return Promise.resolve();
    }

    try {
      debug(
        `Flushing event queue with ${this.eventQueue.length} events (sync: ${sync}, timeout: ${timeoutMs}ms)`,
      );

      // Cancel any pending batch timeout
      if (this.batchTimeout) {
        clearTimeout(this.batchTimeout);
        this.batchTimeout = null;
      }

      // For synchronous processing, wait for all events to complete
      if (sync) {
        // Force a fresh attribution capture before processing events
        this.attributionManager.getAttributionData();
        debug("Refreshed attribution data before flushing queue");

        // Process each event in the queue individually
        const eventsToProcess = [...this.eventQueue];

        // Clear the queue immediately to prevent re-processing
        this.eventQueue = [];
        this.saveQueuedEvents();

        // Create an array of promises for each event processing
        const processingPromises = eventsToProcess.map((event) => {
          // Get fresh metadata with the most current attribution data
          const freshMetadata = this.buildMetadata();

          // Update the event with fresh metadata
          const updatedEvent = {
            ...event,
            data: {
              ...event.data,
              metadata: {
                ...event.data.metadata,
                ...freshMetadata,
              },
            },
          } as TrackableEventWithData;

          // Return the promise for this event's processing
          return this.processEvent(updatedEvent).then((success) => {
            if (success) {
              debug(
                `Successfully processed ${event.eventName} event during sync flush`,
              );
            } else {
              debug(
                `Failed to process ${event.eventName} event during sync flush`,
              );
            }
            return success;
          });
        });

        debug(
          `Waiting for ${processingPromises.length} events to complete processing...`,
        );

        // Create a timeout promise that will resolve after the specified timeout
        const timeoutPromise = new Promise<void>((resolve) => {
          setTimeout(() => {
            debug(
              `Flush timeout of ${timeoutMs}ms reached. Some events may not have been fully processed.`,
            );
            resolve();
          }, timeoutMs);
        });

        // Race between the processing promises and the timeout
        // This ensures we'll either wait for all events to complete OR the timeout to be reached
        try {
          return await Promise.race([
            // Promise that completes when all events are processed
            Promise.all(processingPromises).then((results) => {
              const successCount = results.filter(Boolean).length;
              debug(
                `Completed processing ${successCount}/${results.length} events during sync flush`,
              );
            }),
            // Timeout promise
            timeoutPromise,
          ]);
        } catch (error) {
          debug(
            "Error during synchronized event processing:",
            error instanceof Error ? error.message : String(error),
          );
          // Re-throw as an Error object to satisfy linter
          throw error instanceof Error ? error : new Error(String(error));
        }
      } else {
        // For async processing, use the normal batch process but still return a Promise
        this.processBatch();
        // Return a resolved promise since we don't wait for batch processing to complete
        return Promise.resolve();
      }
    } catch (error) {
      debug(
        "Error flushing queue",
        error instanceof Error ? error.message : String(error),
      );
      // Ensure we're rejecting with an Error object
      return Promise.reject(
        error instanceof Error ? error : new Error(String(error)),
      );
    }
  }

  /**
   * Set the fluid shop identifier
   * @param fluidShop The fluid shop identifier
   */
  public setFluidShop(fluidShop: string): void {
    this.fluidShop = fluidShop;
    debug(`Fluid shop set to: ${fluidShop}`);
  }

  /**
   * Setup interval to periodically flush the event queue
   */
  private setupFlushInterval(): void {
    // Clear any existing interval
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
    }

    // Set up a new interval if auto-flush is enabled in config
    const flushIntervalMs = this.config.flushIntervalMs ?? 30000; // Default to 30 seconds
    if (flushIntervalMs > 0) {
      this.flushInterval = setInterval(() => {
        debug(`Auto-flushing event queue (interval: ${flushIntervalMs}ms)`);
        void this.flushQueue(false);
      }, flushIntervalMs);

      debug(`Set up flush interval: ${flushIntervalMs}ms`);
    }
  }
}
